// Import required components and hooks
import React, { useState, useContext } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { DialogContext } from '../context/DialogContext.js';

// Import GraphQL Mutation
import { useMutation, gql } from '@apollo/client';

// Import languages and utilities
import languages from '../languages.js';
import { Box } from '@mui/material';
import TagsInput from './dialoginputs/TagsInput.js';
import UserContext from '../context/UserContext.js';
import TranslateIcon from '@mui/icons-material/Translate';
import { getDeeplLanguageAcronym } from '../query/deeplLanguages.js';
import UsersInput from './dialoginputs/UsersInput.js';

// Define GraphQL mutations
const TRANSLATE_TEXT_MUTATION = gql`
  mutation TranslateText($text: String!, $sourceLang: String!, $targetLang: String!) {
    translateText(text: $text, sourceLang: $sourceLang, targetLang: $targetLang) {
      text
    }
  }
`;

const ADD_SENTENCE_MUTATION = gql`
  mutation AddUserSentence(
    $username: String!
    $sourceText: String!
    $targetText: String!
    $sourceLang: String!
    $targetLang: String!
    $tags: [String]!
    $sharedWith: [String]!
  ) {
    AddUserSentence(
      username: $username
      sourceText: $sourceText
      targetText: $targetText
      sourceLang: $sourceLang
      targetLang: $targetLang
      tags: $tags
      sharedWith: $sharedWith
    ) {
      sentence {
        id
        text
        lang
      }
      translations {
        id
        text
        lang
      }
    }
  }
`;

// Create the AddSentenceDialog component
function AddSentenceDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext);
  const [sourceLanguage, setSourceLanguage] = useState(Object.keys(languages)[0]);
  const [targetLanguage, setTargetLanguage] = useState(Object.keys(languages)[5]);
  const [sourceSentence, setSourceSentence] = useState('');
  const [targetSentence, setTargetSentence] = useState('');
  const [users, setUsers] = useState(['David']);
  const [tags, setTags] = useState([]);
  const { username } = useContext(UserContext);

  // GraphQL mutations
  const [addUserSentence, { loading: saveLoading }] = useMutation(ADD_SENTENCE_MUTATION);
  const [translateTextMutation, { loading: translateLoading }] = useMutation(TRANSLATE_TEXT_MUTATION);

  const handleClose = () => {
    closeDialog('dialogAddSentence');
  };

  const handleSourceLanguageChange = (event) => {
    setSourceLanguage(event.target.value);
  };

  const handleTargetLanguageChange = (event) => {
    setTargetLanguage(event.target.value);
  };

  const handleSourceSentenceChange = (event) => {
    setSourceSentence(event.target.value);
  };

  const handleTargetSentenceChange = (event) => {
    setTargetSentence(event.target.value);
  };

  const handleSave = async () => {
    try {
      await addUserSentence({
        variables: {
          username,
          sourceText: sourceSentence,
          targetText: targetSentence,
          sourceLang: sourceLanguage,
          targetLang: targetLanguage,
          tags,
          sharedWith: users,
        },
      });
      handleClose();
      setSourceSentence('');
      setTargetSentence('');
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save sentence.');
    }
  };

  const translateText = async () => {
    const sourceLangCode = getDeeplLanguageAcronym(sourceLanguage);
    const targetLangCode = getDeeplLanguageAcronym(targetLanguage);

    if (!sourceLangCode || !targetLangCode) {
      console.error('Translation not supported for selected language');
      alert('Selected language is not supported.');
      return;
    }

    try {
      const { data } = await translateTextMutation({
        variables: {
          text: sourceSentence,
          sourceLang: sourceLangCode,
          targetLang: targetLangCode,
        },
      });
      setTargetSentence(data.translateText.text);
    } catch (error) {
      console.error('Translation error:', error);
      alert('Failed to translate. Please try again.');
    }
  };

  return (
    <Dialog open={dialogStates['dialogAddSentence']} onClose={handleClose}>
      <DialogTitle>Add a new sentence</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="sourceSentence"
          label="Source Sentence"
          type="text"
          fullWidth
          value={sourceSentence}
          onChange={handleSourceSentenceChange}
        />
        <Select
          labelId="source-language-label"
          id="sourceLanguage"
          value={sourceLanguage}
          onChange={handleSourceLanguageChange}
          fullWidth
        >
          {Object.entries(languages).map(([key, value]) => (
            <MenuItem key={key} value={key}>
              {value}
            </MenuItem>
          ))}
        </Select>
        <DialogActions>
          <Button
            onClick={translateText}
            disabled={translateLoading || !sourceSentence}
          >
            Translate <TranslateIcon />
          </Button>
        </DialogActions>
        <TextField
          margin="dense"
          id="targetSentence"
          label="Target Sentence"
          type="text"
          fullWidth
          value={targetSentence}
          onChange={handleTargetSentenceChange}
        />
        <Select
          labelId="target-language-label"
          id="targetLanguage"
          value={targetLanguage}
          onChange={handleTargetLanguageChange}
          fullWidth
        >
          {Object.entries(languages).map(([key, value]) => (
            <MenuItem key={key} value={key}>
              {value}
            </MenuItem>
          ))}
        </Select>
        <Box mt={2}>
          <TagsInput
            selectedTags={setTags}
            fullWidth
            variant="outlined"
            id="tags"
            name="tags"
            placeholder="Add Tags"
            label="Tags"
          />
        </Box>
        <Box mt={2}>
          <UsersInput
            selectedUsers={setUsers}
            users={users}
            fullWidth
            variant="outlined"
            id="users"
            name="users"
            placeholder="Type username"
            label="Share with"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saveLoading || !sourceSentence || !targetSentence}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddSentenceDialog;